import { clsx, type ClassValue } from "clsx";
import { customAlphabet } from "nanoid";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function toBase64(file: File) {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.readAsDataURL(file);

    fileReader.onload = () => {
      resolve(fileReader.result);
    };

    fileReader.onerror = (error) => {
      reject(error);
    };
  });
}

export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number;
    sizeType?: "accurate" | "normal";
  } = {},
) {
  const { decimals = 0, sizeType = "normal" } = opts;

  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const accurateSizes = ["Bytes", "KiB", "MiB", "GiB", "TiB"];
  if (bytes === 0) return "0 Byte";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${
    sizeType === "accurate"
      ? (accurateSizes[i] ?? "Bytest")
      : (sizes[i] ?? "Bytes")
  }`;
}

export const formatNumber = (value: string | number | undefined) => {
  if (!value) return "0";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("en-US").format(num);
};

export const formatCurrency = (
  amount?: string | number,
  currency?: string,
  divideBy100 = true,
) => {
  if (amount === undefined || amount === null) return "--";
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
  }).format(divideBy100 ? numAmount / 100 : numAmount); // Facebook amounts are in cents
};

export const findScrollableParent = (
  element: HTMLElement,
): HTMLElement | null => {
  let parent = element.parentElement;
  while (parent) {
    const overflowY = window.getComputedStyle(parent).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
};

// Extract numeric ID from Shopify GID (e.g., "gid://shopify/Product/123" -> "123")
export const extractNumericId = (gid: string | number) => {
  const parts = String(gid).split("/");
  return parts[parts.length - 1];
};

// Helper to normalize productId to gid format
// If it's already a gid, use it as-is; otherwise construct it
export const getProductGid = (productId: string): string => {
  if (productId.startsWith("gid://")) {
    return productId;
  }
  return `gid://shopify/Product/${productId}`;
};

// Helper to check if an option name represents a color option
export const isColorOptionName = (optionName: string): boolean => {
  const name = optionName.toLowerCase();
  return name === "color" || name === "colour" || name === "shade";
};

export const getFaviconUrl = (domain: string) => {
  const cleanDomain = domain.replace(/^https?:\/\//, "").split("/")[0];
  return `https://www.google.com/s2/favicons?domain=${cleanDomain}&sz=128`;
};

export const randomString = (size = 21) => {
  return customAlphabet(
    "1234567890abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ",
    size,
  )();
};
